import { getFallbackMenuResponse } from '@/api/fallbackResponse';
import { useBffMenu } from '@/hooks/useBffMenu';
import { useI18n } from '@/hooks/useI18n';
import { SCOPE_CLASS, usePortalScope } from '@/hooks/usePortalScope';
import { useLanguageSwitch } from '@/hooks/useLanguageSwitch';
import type { BffMenuResponse } from '@/types/bff';
import { getFlag, initFeatureFlags } from '@/utils/featureFlag';
import { startLogoutRelay, stopLogoutRelay } from '@/utils/logoutRelay';
import { User } from '@vaisala/rockhopper-icons/icons/User';
import { Menu } from '@vaisala/rockhopper-vaisala-com-components/Menu/index';
import '@vaisala/rockhopper-vaisala-com-components/styles.css';
import '@/styles/rem-base.css';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import styles from './HankoMenu.module.scss';
import { LoadingView } from './LoadingView';
import { MenuHeader } from './MenuHeader';
import { MenuItem } from './MenuItem';

// NOTE: The cart is temporarily disabled in the Hanko menu (business decision),
// mirroring the API change that stops returning cart data. The cart UI (icon,
// dropdown, empty-cart dialog), its hooks (useCartDropdown, useEmptyCart), the
// showCartInfoInHankoMenu feature-flag gating, cart validation and background
// polling were removed. See git history to restore them when the cart returns.

const MAX_MENU_ITEMS = 50;

/** Default `menuOffset` — the gap that was hardcoded before this prop existed. */
const DEFAULT_MENU_OFFSET_PX = 24;

/**
 * Clamps the requested gap to a non-negative finite number, falling back to the
 * default. Guards against `NaN`/negatives that would break layout or clicks.
 */
function resolveMenuOffset(requested: number | undefined): number {
  return typeof requested === 'number' && Number.isFinite(requested) && requested >= 0
    ? requested
    : DEFAULT_MENU_OFFSET_PX;
}

export interface HankoMenuProps {
  /** Initial language for i18n bundle selection and BFF API requests. Defaults to 'en'. */
  lang?: string;
  /** Gap (px) below the trigger, for clearing your host navbar. @default 24 */
  menuOffset?: number;
  /**
   * Color for the trigger icons. Resolution: this prop →
   * `--hanko-menu-icon-color` CSS var → `inherit` (navbar text color).
   */
  iconColor?: string;
}

function HankoMenuContent(props: Readonly<HankoMenuProps>) {
  const { locale } = useLanguageSwitch({ initialLang: props.lang });
  const menuOffset = resolveMenuOffset(props.menuOffset);
  const rootStyle = props.iconColor
    ? ({ '--hanko-menu-icon-color': props.iconColor } as CSSProperties)
    : undefined;
  const { t } = useI18n(locale);
  // Cart disabled: background polling is unnecessary while there is no cart.
  const { data, isLoading } = useBffMenu(locale, { pollInterval: false });

  // Use the localized fallback while loading or before BFF data arrives.
  const menuData: BffMenuResponse | null = isLoading ? null : (data ?? getFallbackMenuResponse(t));

  // Start/stop WebSocket logout relay when user auth state changes (feature-flagged).
  // The relay URL is supplied by the BFF state response (empty disables SLO).
  const userEmail = menuData?.user?.email;
  const wsRelayUrl = menuData?.wsRelayUrl;
  useEffect(() => {
    if (!userEmail || !wsRelayUrl) {
      stopLogoutRelay();
      return;
    }

    let cancelled = false;
    initFeatureFlags().then(() => {
      if (cancelled) return;
      if (getFlag('enableSingleLogout')) {
        startLogoutRelay(userEmail, wsRelayUrl);
      }
    });

    return () => {
      cancelled = true;
      stopLogoutRelay();
    };
  }, [userEmail, wsRelayUrl]);

  // ─── User menu open state ────────────────────────────────────────────────
  // Rockhopper's Menu is controllable: `open` / `onOpenChange` are forwarded
  // straight to Base UI's Menu.Root (Menu.tsx, installed v0.32.0).
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
  }, []);

  // ─── Portal scoping ──────────────────────────────────────────────────────
  // Menu popups portal to document.body, outside the scoped CSS. Observe only
  // while the user menu can be open.
  const rootRef = useRef<HTMLDivElement>(null);
  const getRoot = useCallback(() => rootRef.current, []);
  usePortalScope(isMenuOpen, getRoot);

  // ─── Avatar ──────────────────────────────────────────────────────────────
  const avatarLetter =
    menuData?.isAuthenticated && menuData.user ? menuData.user.name.charAt(0).toUpperCase() : null;

  return (
    <div ref={rootRef} className={`${SCOPE_CLASS} ${styles.container}`} style={rootStyle}>
      <Menu
        open={isMenuOpen}
        onOpenChange={handleMenuOpenChange}
        // `modal` is hardcoded `true` upstream and reachable only via this
        // internal escape hatch. NB: _rootProps is spread AFTER `open`
        // upstream, so never put `open` in here.
        _rootProps={{ modal: false }}
        renderTrigger={(props) => (
          <button
            {...props}
            aria-label={t('menu.ariaUserMenu')}
            type="button"
            className={styles.trigger}
          >
            {avatarLetter ? (
              <span className={styles.avatar}>{avatarLetter}</span>
            ) : (
              <User width={24} height={24} />
            )}
          </button>
        )}
      >
        <Menu.Content placement="bottom-end" sideOffset={menuOffset} className={styles.content}>
          {isLoading || !menuData ? (
            <LoadingView loadingLabel={t('loading')} />
          ) : (
            <>
              {menuData.isAuthenticated && menuData.user && <MenuHeader user={menuData.user} />}
              {menuData.menuItems.slice(0, MAX_MENU_ITEMS).map((item, index) => (
                <MenuItem key={`${item.type}-${index}`} item={item} locale={locale} />
              ))}
            </>
          )}
        </Menu.Content>
      </Menu>
    </div>
  );
}

/**
 * HankoMenu — Universal user menu component for Vaisala web platforms.
 *
 * Shows a user icon that opens a dropdown menu. Menu items are driven by the
 * BFF API response. If the API fails, a fallback response with login/register
 * links (using env URLs) is rendered in the same dropdown.
 *
 * The cart is temporarily disabled (see note at the top of this file).
 *
 * All navigation is handled internally — no callbacks to the host needed.
 */
export function HankoMenu(props: Readonly<HankoMenuProps>) {
  return (
    <ErrorBoundary>
      <HankoMenuContent {...props} />
    </ErrorBoundary>
  );
}
